import moment from 'moment-timezone';
import { validate as validateUuid, v4 as generateUuid } from 'uuid';
import {
    RefundCauses,
    STATIC_ELEMENT_PREFIX,    
} from 'src/app/core/constants';

import {
    ChainPrepaidMovementsRead,
    ChainPrizes,
    Customers,
    Departments,
    Items,
    ItemsCombinations,
    PaymentMethods,
    paymentMethodTypes,
    Printers,
    SaleTransactions,
    Sales,
    SalesCustomer,
    SalesEInvoice,
    SalesItems,
    SalesItemsPriceChanges,
    SalesPayments,
    SalesPriceChanges,
    RoomsTables,
    Rooms,
    Bookings,
    Categories
} from 'tilby-models';

import {
    ConfigurationManagerService,
    ConnectionService,
    CoreStateService,
    EntityBase,
    EntityManagerService,
    EnvironmentInfoService,
    OperatorManagerService,
    ScreenOrientationService,
    StorageManagerService
} from 'src/app/core';

import {
    ActiveSaleStoreService,
    CashregisterStateService,
    EditSaleItemUpdate,
    SalePaymentService,
    SalePrintingUtilsService,
    SaleTransactionUtilsService,
    SaleUtilsService
} from 'src/app/features';

import {
    DocumentPrinterOptions
} from 'src/app/shared/model/document-printer.model';

import {
    Subject,
    filter
} from 'rxjs';

import {
    asyncTimeout,
    keyBy,
    matches,
    pickBy,
    removeFromArray
} from 'src/app/shared/utils';

import {
    OrderSendStatus,
    PaymentMethodsFE,
    SaleMode,
    SaleType,
    SalesCashregister
} from 'src/app/shared/model/cashregister.model';

import {
    Injectable,
    Injector,
    WritableSignal,
    effect,
    inject,
    signal
} from '@angular/core';

import {
    $state,
    $stateParams,
    digitalPaymentsManager,
    documentPrinter,
    FiscalProviders,
    fiscalUtils,
    giftCardPrinter,
    leanPMS,
    prepaidSale,
    restManager,
    util
} from 'app/ajs-upgraded-providers';

import { TranslateService } from '@ngx-translate/core';
import { TilbyCurrencyPipe } from '@tilby/tilby-ui-lib/pipes/tilby-currency';

import {
    AddEditSaleDialogFormParams,
    AlertDialogService,
    BuzzerInputDialogService,
    ConfirmDialogService,
    DocumentPrintersManagerDialogStateService,
    ItemListSelectorDialogService,
    OpenDialogsService,
    SignaturePadDialogService,
    SplitSaleDialogService,
    SelectSaleDialogService,
    AddEditSaleDialogService,
    SplitSaleDialogOptions,
} from 'src/app/dialogs';

import { Exit } from '@tilby/tilby-ui-lib/components/tilby-order';

type LoadSaleOptions = OpenSaleOptions & {
    skipOperatorCheck?: boolean
    skipStore?: boolean
}

type OpenSaleOptions = {
    fromParentSale?: boolean,
    skipResetDocumentData?: boolean
}

type NewSaleOptions = {
    dontSaveSale?: boolean
    dontOpenSale?: boolean
    overrides?: Partial<Sales>
}

type FinishSaleClosingOptions = {
    closeActiveSale?: boolean,
    skipParentCheck?: boolean
}

export type StoreSaleOptions = FinishSaleClosingOptions & {
    isOriginalSplit?: boolean
    payments?: SalesPayments[]
    reason?: string
}

export type DeleteSaleOptions = {
    sale?: Sales,
    reason?: string
    skipClosing?: boolean
}

type EmitDocumentOptions = {
    batchOrderMode?: boolean
}

type EmitDocumentResult = {
    emittedSale: Sales,
    notes?: string[],
} | {
    error: string
} | {
    printError?: string,
    printerId?: number,
}

type lastSaleTotal = {
    final_amount: number,
    change: number,
    id?: number,
    uuid?: string
}

export type ActiveSaleEvent =
    { event: 'sale-opened', data: Sales | undefined } |
    { event: ('sale-parked' | 'sale-closed'), data: Sales } |
    { event: ('item-removed' | 'item-added'), data: SalesItems } |
    { event: 'item-changed', data: { currentSaleItem: SalesItems, previousSaleItem: SalesItems } } |
    { event: 'customer-added', data: SalesCustomer } |
    { event: 'customer-removed' } |
    { event: ('priceChange-removed' | 'priceChange-added'), data: { target: Sales | SalesItems, priceChange: SalesPriceChanges | SalesItemsPriceChanges, targetType: 'sale' | 'sale_item' } } |
    { event: 'use-pricelist', data: { priceList: number | 'default', skipNotify?: boolean, skipQuestion?: boolean } } |
    { event: 'reset-pricelist' } |
    { event: 'payment-in-progress' } |
    { event: 'go-to-payments' } |
    { event: 'sale-deleted', data: Sales } |
    { event: 'sale-saved', data: Sales, closeAfterSave?: boolean } |
    { event: 'sale-stored', data: Sales }

@Injectable({
    providedIn: 'root'
})
export class ActiveSaleService {
    private readonly injector = inject(Injector);
    private readonly $state = inject($state);
    private readonly $stateParams = inject($stateParams);
    private readonly activeSaleStore = inject(ActiveSaleStoreService);
    private readonly addEditSaleDialogService = inject(AddEditSaleDialogService);
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly connection = inject(ConnectionService);
    private readonly DigitalPaymentsManager = inject(digitalPaymentsManager);
    private readonly documentPrinter = inject(documentPrinter);
    private readonly documentPrintersManager = inject(DocumentPrintersManagerDialogStateService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly environmentInfoService = inject(EnvironmentInfoService);
    private readonly FiscalProviders = inject(FiscalProviders);
    private readonly fiscalUtils = inject(fiscalUtils);
    private readonly GiftCardPrinter = inject(giftCardPrinter);
    private readonly openDialogsService = inject(OpenDialogsService);
    private readonly operatorManager = inject(OperatorManagerService)
    private readonly prepaidSale = inject(prepaidSale);
    private readonly restManager = inject(restManager);
    private readonly LeanPMS = inject(leanPMS);
    private readonly salePayment = inject(SalePaymentService);
    private readonly salePrintingUtils = inject(SalePrintingUtilsService);
    private readonly saleUtilsService = inject(SaleUtilsService);
    private readonly signaturePadDialog = inject(SignaturePadDialogService);
    private readonly splitSaleDialog = inject(SplitSaleDialogService);
    private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);
    private readonly translateService = inject(TranslateService);
    private readonly util = inject(util);
    private readonly cashregisterStateService = inject(CashregisterStateService);
    private readonly saleTransactionUtilsService = inject(SaleTransactionUtilsService);
    private readonly buzzerInputDialogService = inject(BuzzerInputDialogService);
    private readonly itemListSelector = inject(ItemListSelectorDialogService);

    constructor(
    ) {
        StorageManagerService.storageUpdates$.pipe(
            filter((data) => data.entityName === 'printers')
        ).subscribe(
            (data) => this.onPrinterUpdates(data.entity as Printers)
        );

        EntityBase.offlineFirstCompletion$.pipe(
            filter((data) => data.entityName === 'sales')
        ).subscribe(
            (data) => this.printSaleGiftcard(data.entity as Sales)
        )

        ActiveSaleStoreService.saleUpdates$.subscribe((data) => {
            const previousSale = this.currentSale;

            this.currentSale = data.currentSale || {} as SalesCashregister;
            this._isActiveSale.set(!!(this.currentSale.uuid));
            this._isEmptySale.set(!(this.currentSale.sale_items?.length));
            this._saleType.set(data.saleType);
            this._saleMode.set(data.saleMode);

            if (previousSale?.uuid && previousSale.uuid === this.currentSale?.uuid) {
                this.printSaleTableVariation(previousSale, this.currentSale);
            }
        });

        ActiveSaleService.printerDocumentDataUpdates$.subscribe((data) => {
            this.printerDocumentData = data;
        });

        effect(() => {
            CoreStateService.lockUIActions.set(
                this.printDocumentInProgress() ||
                this.paymentInProgress()
            );
        }, { allowSignalWrites: true });
    }

    //Private attributes
    private currentSaleItem: { saleUuid: string, uuid: string } | undefined;
    private isCreatingSale?: Promise<Sales>;
    private _isActiveSale = signal(false);
    private _isEmptySale = signal(false);
    private _saleType = signal<SaleType | undefined>(undefined);
    private _saleMode = signal<SaleMode | undefined>(undefined);

    //Public attributes
    public currentSale = this.activeSaleStore.currentSale || {} as Readonly<SalesCashregister>;
    public lastSaleTotal: WritableSignal<lastSaleTotal | null> = signal(null);
    public printerDocumentData?: DocumentPrinterOptions;
    public paymentInProgress = signal(false);
    public printDocumentInProgress = signal(false);
    public lockPaymentButtons = signal(false);
    public readonly isActiveSale = this._isActiveSale.asReadonly();
    public readonly isEmpty = this._isEmptySale.asReadonly();
    public readonly saleMode = this._saleMode.asReadonly();
    public readonly saleType = this._saleType.asReadonly();
    public groupItemUuid?: string = undefined;

    //Observables and Subjects
    public static activeSaleEvents$ = new Subject<ActiveSaleEvent>();
    private static printerDocumentDataUpdates = new Subject<DocumentPrinterOptions>();
    public static printerDocumentDataUpdates$ = ActiveSaleService.printerDocumentDataUpdates.asObservable();

    private sendActiveSaleEvent(event: ActiveSaleEvent) {
        ActiveSaleService.activeSaleEvents$.next(event);
    }
    /*
        ### HOOKS ###
    */

    private onPrinterUpdates(printer?: Printers) {
        if (printer?.id && this.printerDocumentData?.printer.id === printer.id) {
            this.resetDocumentData();
        }
    }

    private async printSaleTableVariation(previous: Sales, current: Sales) {
        if (!this.configurationManagerService.getPreference('cashregister.print_order_header_variations')) {
            return;
        }

        if ((!previous.table_id && !current.table_id) || (previous.table_id === current.table_id)) {
            return;
        }

        await this.salePrintingUtils.printSaleTableVariation({
            previous: {
                table_id: previous.table_id,
                table_name: previous.table_name,
                room_id: previous.room_id,
                room_name: previous.room_name
            },
            current: {
                table_id: current.table_id,
                table_name: current.table_name,
                room_id: current.room_id,
                room_name: current.room_name
            },
            print_type: 'table_variation'
        }, current);
    }

    private async printSaleGiftcard(sale: Sales) {
        if (!["closed", "stored"].includes(sale.status)) {
            return;
        }

        const departments = await this.entityManagerService.departments.fetchCollectionOffline();
        const departmentGiftCardsMap = keyBy(departments.filter((department) => department.giftcard_type_uuid), (department) => department.id);

        //Find an item that is linked to a giftcard
        const hasGiftCard = !!(sale.sale_items?.find((saleItem) => (departmentGiftCardsMap[saleItem.department_id])));

        if (!hasGiftCard) {
            return;
        }

        const targetPrinter = sale.sale_documents?.[0]?.printer_id;

        if (!targetPrinter) {
            return;
        }

        this.GiftCardPrinter.printSaleGiftCards(sale.uuid, targetPrinter);
    }

    /*
        ### PRIVATE METHODS ###
    */

    private isEInvoiceDocument(documentTypeId: string) {
        return ['e_invoice', 'summary_e_rc', 'summary_e_nrc'].includes(documentTypeId);
    }

    private saveSaleToStorage = (sale?: Sales) => {
        if (!sale) {
            return this.activeSaleStore.saveSale();
        } else {
            if (sale.id) {
                return this.entityManagerService.sales.putOneOfflineFirst(sale);
            } else {
                return this.entityManagerService.sales.postOneOfflineFirst(sale);
            }
        }
    }

    private async awaitSale() {
        if (this.isActiveSale()) {
            return;
        }

        if (this.isCreatingSale) {
            await this.isCreatingSale;
            return;
        }

        await this.createNewSale();
    }

    private loadParentSaleIfPresent = async (sale: Sales) => {
        const isChildSale = sale.final_amount! >= 0 && !!sale.sale_parent_uuid && sale.uuid !== sale.sale_parent_uuid;

        if (isChildSale) {
            const parentSale = await this.loadSale(sale.sale_parent_uuid, { fromParentSale: true, skipOperatorCheck: true });

            if (parentSale?.id) {
                //Workaround iOS modals bug when transitioning between states
                if(this.environmentInfoService.isAppleMobile()) {
                    await this.$state.transition?.promise;
                    await asyncTimeout(500);
                }

                this.splitSale({ type: parentSale._split_sale_data?.type });
            }
        }

        return isChildSale;
    }

    private finishSaleClosing = async (sale: Sales, options?: FinishSaleClosingOptions) => {
        let isChildSale;

        //Reset document data if we are closing an active sale (avoid resetting it if we are closing a sale printed by an external event)
        if (!this.isActiveSale() || this.currentSale.uuid === sale.uuid) {
            this.resetDocumentData();
        }

        //Actions on closing an active sale
        if (this.isActiveSale() && this.currentSale.uuid === sale.uuid) {
            if (options?.closeActiveSale !== false) {
                //@ts-expect-error
                this.currentSale.status = sale.status;
                await this.openSale(undefined, { skipResetDocumentData: true });
            }

            if (!options?.skipParentCheck) {
                isChildSale = await this.loadParentSaleIfPresent(sale);
            }

            if (isChildSale === false) { //If not a child sale (and we did not skip parent check)
                if (['closed', 'stored'].includes(sale.status) && sale.table_id && this.configurationManagerService.getPreference('cashregister.keep_table_open')) {
                    let saleCustomer;

                    //Remove id from sale customer
                    if (sale.sale_customer) {
                        let { id, ...sc } = sale.sale_customer;
                        saleCustomer = sc;
                    }

                    await this.createNewSale(undefined, {
                        dontOpenSale: true,
                        overrides: {
                            booking_id: sale.booking_id,
                            booking_external_id: sale.booking_external_id,
                            covers: sale.covers,
                            order_type: 'normal',
                            room_id: sale.room_id,
                            room_name: sale.room_name,
                            table_id: sale.table_id,
                            table_name: sale.table_name,
                            sale_customer: saleCustomer
                        }
                    });

                    this.openDialogsService.openSnackBarTilby('CASHREGISTER.ACTIVE_SALE.TABLE_KEPT_OPEN', 'MISC.OK', { duration: 5000 });
                }

                if(this.configurationManagerService.getPreference('cashregister.return_to_history_after_summary_emission') && sale.sale_documents?.some(doc => doc.document_type.startsWith('summary_e_'))) {
                    this.$state.go('app.new.history.sales', { showToBeInvoiced: true });
                }

                if (this.$stateParams.autohide) {
                    this.$state.go('.', { autohide: null });
                    this.util.minimizeApp();
                } else if (this.configurationManagerService.getPreference('cashregister.open_tables_after_send')) {
                    this.goToTablesSelectedRoom(sale.room_id, sale.order_type);
                }
            }
        }
    }

    public async goToTablesSelectedRoom(roomId: number | Omit<Sales.OrderTypeEnum, 'normal'> | undefined, order_type: Sales.OrderTypeEnum | undefined) {
        if ((order_type == 'take_away' || order_type == 'delivery') && this.configurationManagerService.getPreference('orders.return_tables_takeaway_delivery')) {
            roomId = order_type;
        }


        if (order_type === 'delivery' || order_type === 'take_away') {
            if (this.configurationManagerService.getPreference('orders.return_tables_takeaway_delivery')) {
                this.$state.go("app.new.delivery-take-away");

                // Deselect sale after send click for delivery and take away
                await this.loadSale(undefined);
            }
        } else {
            if (roomId) {
                this.goToTables(roomId);
            }
        }
    }

    public async goToTables(roomId: number | Omit<Sales.OrderTypeEnum, 'normal'>) {
        if (!roomId || !this.configurationManagerService.isModuleEnabled('tables')) {
            return;
        }

        await this.loadSale(undefined);

        if (!this.configurationManagerService.getSettingUserFirst('cashregister.use_tables_map_in_portrait') && this.injector.get(ScreenOrientationService).getOrientation().includes("portrait")) {
            const res = await this.injector.get(SelectSaleDialogService).openDialog();

            if (!res?.saleId) {
                return;
            }

            await this.loadSale(res.saleId);
        } else {
            this.$state.go("app.new.tables.rooms-view", { id: roomId });
        }
    }

    private async sendStoredSaleToPMS(sale: Sales, options?: StoreSaleOptions) {
        const saleToPMS = structuredClone(sale);

        if (options?.isOriginalSplit) {
            saleToPMS.is_summary = true;
        } else {
            const cashPayment = await this.getCashPayment(saleToPMS);
            saleToPMS.payments = [(cashPayment)];
        }

        await this.documentPrinter.sendSaleToPMS(saleToPMS);
    }

    private performStore = async (saleToStore: Sales, options?: StoreSaleOptions) => {
        const opData = this.operatorManager.getOperatorData();

        Object.assign(saleToStore, {
            status: 'stored',
            closed_at: new Date().toISOString(),
            payments: options?.payments,
            seller_id: opData.id,
            seller_name: opData.full_name,
        });

        if (options?.reason) {
            saleToStore.notes = options.reason;
        }

        await this.saleUtilsService.calculateSaleItemsCosts(saleToStore);

        //Save sale in offline-first mode
        const sale = await this.saveSaleToStorage(saleToStore);

        //Send sale to PMS
        this.sendStoredSaleToPMS(saleToStore, options);

        //Finish sale closing
        this.finishSaleClosing(sale!, options);
    }

    private addGenericPriceChange = (target: Sales | SalesItems, type: SalesPriceChanges.TypeEnum, value: number, description: string, index?: number, overrides?: Partial<SalesPriceChanges | SalesItemsPriceChanges>) => {
        const priceChange = this.saleUtilsService.getGenericPriceChange(target, type, value, description, index, overrides);

        // Check if we are dealing with a sale or a sale item by checking if the target has a 'open_at' property
        if ('open_at' in target) {
            // Target is a sale
            this.activeSaleStore.addSalePriceChanges(priceChange);
        } else {
            // Target is a sale item
            this.activeSaleStore.addSaleItemPriceChanges(target.uuid, priceChange);
        }

        this.sendActiveSaleEvent({
            event: 'priceChange-added',
            data: {
                target: target,
                priceChange: priceChange,
                targetType: 'open_at' in target ? 'sale' : 'sale_item'
            }
        });

        return priceChange;
    };

    public async determinePricelistToUse(sale?: Sales, skipNotify?: boolean, skipQuestion?: boolean) {
        const priceListToUse = await this.saleUtilsService.getSalePriceList(sale);

        if (priceListToUse) {
            this.sendActiveSaleEvent({
                event: 'use-pricelist',
                data: {
                    priceList: priceListToUse,
                    skipNotify,
                    skipQuestion
                }
            });
        } else if (this.configurationManagerService.getPreference("cashregister.reset_pricelist_on_close")) {
            this.sendActiveSaleEvent({
                event: 'reset-pricelist'
            });
        } else {
            this.sendActiveSaleEvent({
                event: 'use-pricelist',
                data: {
                    priceList: 'default',
                    skipNotify,
                    skipQuestion
                }
            });
        }
    };

    public async openSale(saleData?: Sales | SaleTransactions[], options?: OpenSaleOptions) {
        //Reset status variables
        this.resetGroupItemUuid();
        this.currentSaleItem = undefined;

        //Emit sale-parked event if we are unloading a current open sale
        if (this.isActiveSale() && !['closed', 'stored'].includes(this.currentSale.status)) {
            this.sendActiveSaleEvent({ event: 'sale-parked', data: this.currentSale });
        }

        let sale;

        try {
            sale = await this.activeSaleStore.openSale(saleData);
        } catch (err) {
            //Avoid showing this error when loading a parent sale, since there is a chance that the parent sale is not in open status (e.g. for split sales)
            if (!options?.fromParentSale) {
                this.alertDialogService.openDialog({ data: { messageLabel: `CASHREGISTER.ACTIVE_SALE.${err}` } });
            }

            return;
        }

        await this.determinePricelistToUse(sale, true);

        this.sendActiveSaleEvent({ event: 'sale-opened', data: sale });

        if (!this.printerDocumentData && options?.skipResetDocumentData !== true) {
            this.resetDocumentData();
        }

        if (sale) {
            this.lastSaleTotal.set(null);
        }

        return (sale || null);
    };

    private async askSellerIfRequired() {
        if (
            this.configurationManagerService.getShopPreference('cashregister.ask_seller_for_each_sale')
            && !this.configurationManagerService.isUserPermittedOptIn('skip_ask_seller_for_each_sale')
        ) {
            await this.operatorManager.changeOperator();
        }
    };

    public getFastPaymentAmount(sale: Sales, paymentMethod: PaymentMethods, paymentAmount?: number): number {
        return paymentAmount || this.salePayment.getTotalAmountToPay(sale, { targetPayment: paymentMethod }) || 0;
    };

    /*
        ### PUBLIC METHODS ###
    */

    public resetGroupItemUuid() {
        this.groupItemUuid = undefined;
    }

    public isPristine() {
        return this.activeSaleStore.isPristine();
    }

    public async loadSale(saleId?: number | string, options?: LoadSaleOptions) {
        if (saleId && !options?.skipOperatorCheck) {
            //Wait for operator change if necessary
            await this.askSellerIfRequired();
        }

        //Save current open sale if necessary
        if (options?.skipStore !== true && this.isActiveSale() && !this.isPristine()) {
            await this.saveSaleToStorage();
        }

        const [saleData] = await this.activeSaleStore.loadSale(saleId);

        return this.openSale(saleData, options);
    }

    private async openSaleDetailsDialog(isEdit: boolean, params: AddEditSaleDialogFormParams) {
        const rooms = await this.entityManagerService.rooms.fetchCollectionOffline();

        let tables: (RoomsTables & { room: Rooms })[] = [];

        for (const room of rooms) {
            for (const table of room.tables || []) {
                tables.push(Object.assign({
                    room: room
                }, table));
            }
        }

        //Remove static elements (chairs, walls, plants, etc.)
        tables = tables.filter((table) => (!table.shape.startsWith(STATIC_ELEMENT_PREFIX)));

        let res = undefined;

        const skipDialog = params.room?.skip_creation_dialog ?? this.configurationManagerService.getPreference('orders.skip_creation_dialog');

        if ((skipDialog && !params.longPress) && !params.id && params.saleType === 'normal' && params.table?.id) {
            res = { name: this.translateService.instant('TABLES_NEW.DIALOGS.SALE.NAME_TEMPLATE'), type: params.saleType, table_id: params.table.id, covers: params.booking?.people || params.table.covers };
        } else {
            res = await this.addEditSaleDialogService.openDialog({
                formParams: params,
                isEdit: isEdit,
                tables: tables
            });
        }

        if (!res) {
            return;
        }

        const updateData: Partial<Sales> = {};

        Object.assign(updateData, {
            name: res.name,
            order_type: res.type,
            notes: res.notes
        });

        switch (res.type) {
            case 'normal':
                const table = tables.find((table) => table.id === res.table_id);

                Object.assign(updateData, {
                    table_id: table?.id,
                    table_name: table?.name,
                    room_id: table?.room?.id,
                    room_name: table?.room?.name,
                    covers: res.covers,
                    booking_id: res.booking_id || null,
                    deliver_at: undefined,
                    external_id: undefined,
                    channel: undefined
                });
                break;
            case 'take_away':
            case 'delivery':
                Object.assign(updateData, {
                    table_id: undefined,
                    table_name: undefined,
                    room_id: undefined,
                    room_name: undefined,
                    covers: undefined,
                    booking_id: undefined,
                    booking_external_id: undefined,
                    deliver_at: res.delivery_at,
                    external_id: res.type === 'delivery' ? res.order_id : undefined,
                    channel: res.type === 'delivery' ? res.channels : undefined
                });
                break;
        }

        return updateData;
    }

    public async editSale() {
        const params: AddEditSaleDialogFormParams = {
            id: this.currentSale.id,
            name: this.currentSale.name,
            saleType: this.currentSale.order_type,
            table: {
                id: this.currentSale.table_id,
                covers: this.currentSale.covers
            },
            booking: {
                id: this.currentSale.booking_id
            },
            order_id: this.currentSale.external_id,
            delivery_at: this.currentSale.deliver_at,
            channel: this.currentSale.channel,
            notes: this.currentSale.notes
        }

        const updateData = await this.openSaleDetailsDialog(true, params);

        if (!updateData) {
            return;
        }

        this.updateSaleDetails(updateData);

        //Update sale cover
        await this.configureSaleCover(this.currentSale);

        await this.saveSale();
    }

    public async setLockStatus(locked: boolean, options?: { skipSave?: boolean }) {
        if (!!this.currentSale.bill_lock === !!locked) {
            return;
        }

        this.updateSaleDetails({ bill_lock: locked });

        if (!options?.skipSave) {
            await this.saveSale();
        }
    }

    private async configureSaleCover(sale: Sales) {
        const isActiveSale = sale === this.currentSale;

        if (!sale || sale.sale_parent_uuid || !sale.table_id || !sale.covers) {
            return;
        }

        const coverConfig = await this.saleUtilsService.getCoverConfiguration(sale);

        if (!isActiveSale) {
            return this.saleUtilsService.applyCoverToSale(sale, coverConfig);
        }

        sale.sale_items = sale.sale_items || [];

        switch (coverConfig.type) {
            case 'item':
                const currentCoverItem = sale.sale_items?.find((item) => item.item_id === coverConfig.data.id);

                if (currentCoverItem) {
                    this.editSaleItem(currentCoverItem, { quantity: sale.covers })
                } else {
                    const coverSaleItem = await this.saleUtilsService.getCoverSaleItem(sale, coverConfig.data, sale.covers);
                    this.activeSaleStore.addSaleItem(coverSaleItem);
                }
                break;
            case 'price_change':
                const hasCoverPriceChange = sale.price_changes?.some((priceChange) => matches(priceChange, {
                    type: 'surcharge_perc',
                    description: coverConfig.data.description,
                    value: coverConfig.data.value
                }));

                if (!hasCoverPriceChange) {
                    const coverPriceChange = this.saleUtilsService.getGenericPriceChange(sale, 'surcharge_perc', coverConfig.data.value, coverConfig.data.description);
                    this.activeSaleStore.addSalePriceChanges(coverPriceChange);
                }
                break;
            case 'error':
                break;
            default:
                break;
        }
    }

    public async getBuzzerInputIfRequired(sale: Sales): Promise<{ pager: string | null } | undefined> {
        if (!['normal', 'take_away'].includes(sale.order_type!) || sale.table_id) {
            return { pager: null };
        }

        const requireBuzzerInput = !!this.configurationManagerService.getShopPreference('cashregister.require_buzzer_input');

        if (!requireBuzzerInput) {
            return { pager: null };
        }

        return this.buzzerInputDialogService.openDialog({ data: { title: 'DIALOG.BUZZER_INPUT_DIALOG.TITLE' } });
    }

    /**
     * @deprecated
     * Creates a new sale. Used only for compatiblity with old code.
     * @param options - Optional parameters for creating a new sale.
     * @returns {Promise<Sales>} - A promise that resolves to the newly created sale.
     */
    public async newSale(options?: NewSaleOptions) {
        return this.createNewSale(undefined, options);
    }

    private async _createNewSale(params?: AddEditSaleDialogFormParams, options?: NewSaleOptions): Promise<Sales> {
        // If there is an active sale and we don't want to open the new sale, save it to the storage
        if (!options?.dontOpenSale && this.isActiveSale() && this.currentSale.id) {
            this.saveSaleToStorage();
        }

        // If the sale is being saved (not in kiosk mode) and being opened on the cashregister ask for the current seller
        if (!options?.dontSaveSale && !options?.dontOpenSale) {
            await this.askSellerIfRequired();
        }

        // Open the sale details dialog if required parameters are provided
        let updateData = null;

        let booking: Bookings | undefined;

        if (params) {
            if (params.booking?.id) {
                booking = await this.entityManagerService.bookings.fetchOneOfflineFirst(params.booking.id);

                if (booking?.booking_customer) {
                    Object.assign(params, { customer: booking.booking_customer });
                }
            }

            updateData = await this.openSaleDetailsDialog(false, params);

            // If the sale details dialog is cancelled, return without creating a new sale
            if (!updateData) {
                throw 'SALE_CREATION_CANCELLED';
            }
        }

        // Create a new sale object with default values and any provided update data or overrides
        let sale: Sales = {
            ...(await this.saleUtilsService.getSaleTemplate()),
            ...(updateData || {}),
            ...(options?.overrides || {}),
        }

        // Configure the sale cover if necessary
        await this.configureSaleCover(sale);

        this.saleUtilsService.calculateSalePrices(sale);

        // If the sale is being saved (not in kiosk mode), ask for the current pager
        if (!options?.dontSaveSale) {
            //TODO: once we use this service in kiosk mode we should merge pager handling in one service
            const buzzer = await this.getBuzzerInputIfRequired(sale);

            // Cancel sale creation if the user cancels the buzzer input dialog
            if (!buzzer) {
                throw 'SALE_CREATION_CANCELLED';
            }

            sale.name = buzzer.pager ? `Pager ${buzzer.pager}` : sale.name;

            // Check for LeanPMS and add PMS data if the user selects them
            const isLeanPMSEnabled = await this.LeanPMS.isSetup();

            if (isLeanPMSEnabled && this.configurationManagerService.getPreference('lean_pms.ask_room_number_on_sale_open')) {
                const reservationData = await this.LeanPMS.getSaleReservationData(sale);

                if (reservationData) {
                    Object.assign(sale, reservationData);
                }
            }

            // Save the sale
            sale = await this.saveSaleToStorage(sale) as Sales;

            // Load the sale if not asked otherwise
            if (!options?.dontOpenSale) {
                await this.loadSale(sale.id, { skipOperatorCheck: true });
            }
        } else {
            // Load the sale (only in memory)
            if (!options?.dontOpenSale) {
                await this.openSale(sale);
            }
        }

        if (booking) {
            await this.entityManagerService.bookings.putOneOfflineFirst({
                ...booking,
                status: 'arrived'
            });
        }

        if (options?.dontOpenSale) {
            return sale;
        }

        // If a customer is provided in the parameters, add it to the customer list
        if (params?.customer) {
            await this.cashregisterStateService.addCustomer(params.customer);
        }

        // Resolve the promise with a clone of the current sale
        return structuredClone(this.currentSale);
    }

    /**
     * Creates a new sale.
     *
     * This function is responsible for creating a new sale. It handles various aspects of the sale creation process,
     *
     * @param {AddEditSaleDialogFormParams} params - Optional parameters for opening the sale details dialog.
     * @param {NewSaleOptions} options - Optional parameters for creating a new sale.
     * @returns {Promise<Sales>} - A promise that resolves to the newly created sale.
     * @throws {string} - Throws an error with the message 'SALE_CREATION_PENDING' if a sale is currently being created.
     */
    public async createNewSale(params?: AddEditSaleDialogFormParams, options?: NewSaleOptions): Promise<Sales> {
        // Check if a sale creation is already in progress
        if (this.isCreatingSale) {
            // Throw an error indicating that a sale creation is pending
            throw 'SALE_CREATION_PENDING';
        }

        // Create a new promise that represents the sale creation process
        this.isCreatingSale = this._createNewSale(params, options).finally(() => {
            this.isCreatingSale = undefined;
        });

        // Return the promise that represents the sale creation process
        return this.isCreatingSale;
    }

    public async saveSale(newName?: string, closeAfterSave?: boolean) {
        if (newName && newName !== this.currentSale.name) {
            this.updateSaleDetails({ name: newName });
        }

        const sale = await this.saveSaleToStorage();

        if (closeAfterSave) {
            await this.openSale();
        }

        this.sendActiveSaleEvent({ event: 'sale-saved', data: structuredClone(sale!), closeAfterSave });
    }

    public async storeSale(options?: StoreSaleOptions) {
        if (!Object.keys(this.currentSale || {}).length) {
            return;
        }
        
        const saleToSend = structuredClone(this.currentSale);
        const saleToPrint = structuredClone(saleToSend);
        let payments: SalesPayments[] | undefined;

        if (saleToSend.final_amount! > 0) {
            // Get supported cashdrawers
            const cashDrawerMethods = await this.entityManagerService.paymentMethods.fetchCollectionOffline({ payment_method_type_id_in: this.DigitalPaymentsManager.getCahdrawerMethods() })
                .then((paymentMethods) => paymentMethods.filter((paymentMethod) => this.DigitalPaymentsManager.isPaymentDigitalEnvironmentAllowed(paymentMethod.payment_method_type_id)));

            if (cashDrawerMethods.length) {
                let targetPaymentMethod: PaymentMethods | undefined;

                const defaultPaymentMethodIdPref = this.configurationManagerService.getPreference('cashregister.storedpayment');

                //If there is a default id, check if the related payment method exists for this class
                if (defaultPaymentMethodIdPref) {
                    const defaultPaymentMethodId = parseInt(defaultPaymentMethodIdPref) || 0;

                    targetPaymentMethod = cashDrawerMethods.find((paymentMethod) => paymentMethod.id === defaultPaymentMethodId);
                }

                if (!targetPaymentMethod) {
                    if (cashDrawerMethods.length === 1) {
                        targetPaymentMethod = cashDrawerMethods[0];
                    } else {
                        const selectResult = await this.itemListSelector.openDialog({
                            data: {
                                message: 'CASHREGISTER.ACTIVE_SALE.SELECT_CASH_DRAWER',
                                items: cashDrawerMethods,
                                options: {
                                    defaultElement: targetPaymentMethod
                                }
                            }
                        });

                        if (!selectResult) {
                            return;
                        }

                        //Save the selection if the user wants so
                        if (selectResult.setAsDefault) {
                            this.configurationManagerService.setUserPreference('cashregister.storedpayment', selectResult.element.id);
                        }

                        targetPaymentMethod = selectResult.element;
                    }
                }

                if (targetPaymentMethod) {
                    const saleAmount = this.salePayment.getTotalAmountToPay(saleToSend, { targetPayment: targetPaymentMethod });

                    const result = await this.DigitalPaymentsManager.digitalPayment(saleAmount, targetPaymentMethod.id, { sale: structuredClone(saleToSend) })
                        .then(r => Array.isArray(r) ? r : [r]);

                    const now = new Date().toISOString();

                    //@ts-expect-error
                    payments = result.map((payment) => ({
                        ...payment,
                        date: payment.date || now,
                        amount: payment.amount || saleAmount,
                        paid: true,
                    }));
                }
            }

            this.sendActiveSaleEvent({
                event: 'sale-stored',
                data: {
                    ...structuredClone(saleToSend),
                    status: 'stored'
                }
            });
        }

        this.lastSaleTotal.set({
            final_amount: saleToSend.final_amount || 0,
            change: saleToSend.change || 0,
        });

        await this.performStore(saleToSend, { ...options, payments });

        if (this.configurationManagerService.getPreference('cashregister.print_nonfiscal_sale_on_store')) {
            this.salePrintingUtils.sendSaleAsOrder(saleToPrint);
        }

        if (this.printerDocumentData?.printer.id && this.configurationManagerService.getPreference('cashregister.open_cashdrawer_on_store')) {
            this.documentPrinter.openCashDrawer(this.printerDocumentData);
        }
    }

    public async deleteSale(options?: DeleteSaleOptions) {
        if (options?.reason) {
            this.updateSaleDetails({ notes: options.reason });
        }

        const clonedCurrentSale = structuredClone(options?.sale || this.currentSale);

        //Delete sale from the fiscal provider if necessary
        const fiscalProviderDocument = clonedCurrentSale.sale_documents?.find((document) => document.document_type === 'fiscal_provider');

        if (fiscalProviderDocument) {
            const fiscalProviderName = fiscalProviderDocument.meta?.fiscal_provider;
            const fiscalProvider = this.FiscalProviders.getFiscalProvider(fiscalProviderName);

            if (fiscalProvider && 'cancelFiscalDocument' in fiscalProvider) {
                try {
                    await fiscalProvider.cancelFiscalDocument(clonedCurrentSale);
                } catch (error) {
                    if (fiscalProvider.getProviderError) {
                        throw fiscalProvider.getProviderError(error);
                    }

                    throw error;
                }
            }
        }

        try {
            await this.activeSaleStore.deleteSale();
            this.sendActiveSaleEvent({ event: 'sale-deleted', data: clonedCurrentSale });
        } catch (err) { }

        if (options?.skipClosing) {
            return;
        }

        this.finishSaleClosing(clonedCurrentSale);
    }

    public async addItemToSale(item: Items, priceList: number, combinationId?: number, quantity?: number, barcode?: string, overrides?: Partial<SalesItems>, options?: { addAsNew?: boolean }) {
        await this.awaitSale();

        const priceListStr = `price${priceList}` as (`price${number}` & keyof Items & keyof ItemsCombinations);

        //Get target price
        let targetPrice = item[priceListStr];

        const menuMode = this.groupItemUuid && !item.is_group_item;

        if (menuMode) {
            targetPrice = 0;
        }
        else if (overrides?.price != null && Number.isFinite(overrides?.price)) {
            targetPrice = overrides.price;
        }
        else if (combinationId) {
            const combination = item.combinations?.find((combination) => combination.id === combinationId);
            targetPrice = combination ? combination[priceListStr] : item[priceListStr];
        }

        const editExistingItem =
            !options?.addAsNew &&
            !this.configurationManagerService.getPreference("cashregister.add_item_as_new") &&
            (!Object.keys(overrides || {}).length || (Object.keys(overrides || {}).length == 1 && overrides?.exit)) &&
            quantity == null &&
            !item.is_group_item &&
            !item.split_group_components

        if (editExistingItem) {
            //Check if the item is already in the sale
            const saleItem = this.currentSale.sale_items?.find((saleItem) => (
                saleItem.type === 'sale' &&
                saleItem.item_id === item.id &&
                (overrides ? saleItem.exit === overrides.exit : true) &&
                saleItem.sale_item_parent_uuid == this.groupItemUuid &&
                saleItem.combination_id == combinationId &&
                saleItem.price === targetPrice &&
                !(saleItem.variations?.length) &&
                !(saleItem.ingredients?.length)
            ));

            if (saleItem?.item_id) {
                return this.editSaleItem(saleItem, { quantity: (saleItem.quantity === -1) ? 1 : (saleItem.quantity + 1) });
            }
        }

        const { price: itemPrice, ...itemOverrides } = overrides || {};

        const saleItem = await this.saleUtilsService.getSaleItemTemplate(item, priceList, combinationId, quantity, barcode, Object.assign(itemOverrides, {
            price: targetPrice,
            sale_item_parent_uuid: menuMode ? (this.groupItemUuid || null) : null
        })) as SalesItems;

        if (!saleItem?.quantity) {
            return saleItem;
        }

        const itemsToAdd = [];

        if (item.is_group_item || item.split_group_components) {
            this.groupItemUuid = saleItem.uuid;
        }

        //Handle menus with components splitting
        if (item.split_group_components) {
            //Find the items linked to the menu and add the resulting order items
            // Remove the variations that have a linked item from the sale item
            const variationsWithItem = removeFromArray(saleItem.variations || [], (variation) => !!(variation.linked_item_uuid));

            for (const variation of variationsWithItem) {
                const linkedItemSearch = await this.entityManagerService.items.fetchCollectionOffline({ uuid: variation.linked_item_uuid });
                const defaultExit = item.variations?.find((v) => v.id === variation.variation_id)?.default_exit;
                const quantity = saleItem.quantity * (variation.quantity ?? 1);

                if (linkedItemSearch.length) {
                    const itemToAdd = await this.saleUtilsService.getSaleItemTemplate(linkedItemSearch[0], priceList, undefined, quantity, undefined, { price: variation.price_difference || 0, sale_item_parent_uuid: this.groupItemUuid, exit: defaultExit });

                    if (itemToAdd?.quantity) {
                        itemsToAdd.push(itemToAdd);
                    }
                }
            }

            this.resetGroupItemUuid();
        }

        itemsToAdd.unshift(saleItem);

        for (const itemToAdd of itemsToAdd) {
            this.activeSaleStore.addSaleItem(itemToAdd);
        }

        // If groupItemUuid has value and the disableMenuIngredientsPricing preference is enabled, set the price difference of the variation's ingredients to 0
        this.managePriceDifferenceForIngredients(saleItem);

        this.sendActiveSaleEvent({ event: 'item-added', data: saleItem });

        return saleItem;
    }

    public cloneSaleItem(saleItem?: SalesItems, numClones?: number) {
        const targetSaleItem = this.getActiveSaleItem(saleItem);

        if (!targetSaleItem) {
            return;
        }

        const now = new Date().toISOString();
        const opData = this.operatorManager.getOperatorData();
        const { id, ...newSaleItem } = structuredClone(targetSaleItem);

        if (!numClones) {
            numClones = 1;
        }

        Object.assign(newSaleItem, {
            added_at: now,
            ingredients: this.saleUtilsService.getCleanSubEntity(newSaleItem.ingredients),
            lastupdate_at: now,
            lastupdate_by: opData.id,
            price_changes: this.saleUtilsService.getCleanSubEntity(newSaleItem.price_changes),
            seller_id: opData.id,
            seller_name: opData.full_name,
            variations: this.saleUtilsService.getCleanSubEntity(newSaleItem.variations)
        });

        for (let i = 0; i < numClones; i++) {
            this.activeSaleStore.addSaleItem(Object.assign(structuredClone(newSaleItem), { uuid: generateUuid() }));
        }
    }

    public canChangeSaleItemQuantity(saleItem: SalesItems) {
        return !saleItem.prize_id &&
            !['deposit_cancellation', 'coupon'].includes(saleItem.type) &&
            saleItem.sku !== 'invoice-note';
    }

    public changeSaleItemQuantity(saleItem: SalesItems, newQuantity: number) {
        const targetSaleItem = this.getActiveSaleItem(saleItem);

        if (!targetSaleItem || !Number.isFinite(newQuantity) || !this.canChangeSaleItemQuantity(targetSaleItem)) {
            return;
        }
        
        const updateData: Partial<SalesItems> = {
            quantity: newQuantity,
            type: "sale",
            refund_cause_description: undefined,
            refund_cause_id: undefined
        };

        if(targetSaleItem.request_weighing) {
            updateData.request_weighing = false;
        }

        this.editSaleItem(targetSaleItem, updateData);
    }

    private expandDepartmentData(saleItem: Partial<SalesItems>) {
        if (saleItem.department) {
            Object.assign(saleItem, {
                department_id: saleItem.department.id,
                department_name: saleItem.department.name,
                vat_perc: saleItem.department.vat?.value
            });
        }

        return saleItem;
    }

    public addSaleItem(saleItem: SalesItems) {
        const saleItemToAdd = structuredClone(saleItem);
        const time = new Date().toISOString();
        const opData = this.operatorManager.getOperatorData();

        Object.assign(saleItem, {
            added_at: time,
            lastupdate_at: time,
            lastupdate_by: opData.id,
            seller_id: opData.id,
            seller_name: opData.full_name,
            uuid: generateUuid()
        });

        this.expandDepartmentData(saleItemToAdd);

        this.activeSaleStore.addSaleItem(saleItem);

        this.sendActiveSaleEvent({ event: 'item-added', data: saleItem });

        return saleItem;
    }

    public editSaleItem(saleItem: SalesItems, newData?: Partial<SalesItems>, options?: { overrideMenuChecks?: boolean }) {
        let previousSaleItem = structuredClone(saleItem);

        //Incremental update
        let fieldsToOmit: (keyof SalesItems)[] = [];

        if (!options?.overrideMenuChecks) {
            let isItemASelectedMenu = (this.groupItemUuid && saleItem.uuid === this.groupItemUuid);

            if (isItemASelectedMenu) {
                fieldsToOmit.push('quantity');
            }

            if (saleItem.sale_item_parent_uuid) {
                fieldsToOmit.push('price');
            }
        }

        const updateData = pickBy(newData || {}, (value, key) => !fieldsToOmit.includes(key));

        if (!Object.keys(updateData).length) {
            return saleItem;
        }

        this.expandDepartmentData(updateData);

        const saleItemsToUpdate: EditSaleItemUpdate[] = [{
            saleItem,
            newData: updateData
        }];

        //If we changed the quantity of a menu, change all menu items quantity accordingly
        if (saleItem.is_group_item && updateData.quantity) {
            let multiplierFactor = (updateData.quantity / saleItem.quantity);
            let itemsToChange = this.currentSale.sale_items?.filter((si) => si.sale_item_parent_uuid === saleItem.uuid);

            for (const si of itemsToChange || []) {
                saleItemsToUpdate.push({
                    saleItem: si,
                    newData: { quantity: Math.ceil(si.quantity * multiplierFactor) }
                });
            }
        }
        
        // If groupItemUuid has value and the disableMenuIngredientsPricing preference is enabled, set the price difference of the variation's ingredients to 0
        if(newData) {
            this.managePriceDifferenceForIngredients(newData);
        }

        this.activeSaleStore.editSaleItems(saleItemsToUpdate);

        this.sendActiveSaleEvent({ event: 'item-changed', data: { currentSaleItem: saleItem, previousSaleItem } });

        return this.currentSale.sale_items?.find((si) => si.uuid === saleItem.uuid);
    }

    public managePriceDifferenceForIngredients(saleItem: SalesItems | Partial<SalesItems>) {
        const disableMenuIngredientsPricing = this.configurationManagerService.getPreference('cashregister.disable_menu_ingredients_pricing');

        if(disableMenuIngredientsPricing && saleItem.sale_item_parent_uuid && saleItem.ingredients) {
            for(const ingredient of saleItem.ingredients) {
                ingredient.price_difference = 0;
            }
        }
    }

    public updateSaleDetails(saleDetails: Partial<Sales>) {
        if (!this.isActiveSale()) {
            return;
        }

        this.activeSaleStore.updateSaleDetails(saleDetails);
    }

    public removeSaleItem(saleItem: SalesItems) {
        //Remove target and all children from current sale
        const itemsToRemove = this.currentSale?.sale_items?.filter((si) => si.uuid === saleItem.uuid || si.sale_item_parent_uuid === saleItem.uuid) || [];
        this.activeSaleStore.removeSaleItems(itemsToRemove);

        if (saleItem.uuid === this.groupItemUuid) {
            this.resetGroupItemUuid();
        }

        if (this.currentSaleItem?.uuid && saleItem.uuid === this.currentSaleItem.uuid) {
            this.currentSaleItem = undefined;
        }

        this.sendActiveSaleEvent({ event: 'item-removed', data: saleItem });

        return itemsToRemove;
    }

    public refundSaleItem(saleItem: SalesItems, refundCause: RefundCauses) {
        return this.editSaleItem(saleItem, {
            quantity: -(Math.abs(saleItem.quantity)),
            type: 'refund',
            refund_cause_id: refundCause.id,
            refund_cause_description: this.translateService.instant(refundCause.translation_id),
            price_changes: []
        });
    }

    public cancelRefundOnSaleItem(saleItem: SalesItems) {
        return this.editSaleItem(saleItem, {
            quantity: Math.abs(saleItem.quantity),
            type: 'sale',
            refund_cause_id: undefined,
            refund_cause_description: undefined,
        });
    }

    public removePriceChangeFromSale(priceChange: SalesPriceChanges) {
        this.activeSaleStore.removeSalePriceChange((pc) => pc.index === priceChange.index);

        this.sendActiveSaleEvent({ event: 'priceChange-removed', data: { target: this.currentSale, priceChange: priceChange, targetType: 'sale' } });
    }

    public async removePriceChangeFromSaleItem(saleItem: SalesItems, priceChange: SalesItemsPriceChanges) {
        this.activeSaleStore.removeSaleItemPriceChange(saleItem.uuid, (pc) => pc.index === priceChange.index);

        //Restore default item department if the discount is a gift
        if (priceChange.type === 'gift' && saleItem.item_id) {
            try {
                const originalItem = await this.entityManagerService.items.fetchOneOffline(saleItem.item_id);

                if (originalItem) {
                    this.editSaleItem(saleItem, {
                        department: originalItem.department
                    });
                }
            } catch (err) {
                //Nothing to do
            }
        }

        this.sendActiveSaleEvent({ event: 'priceChange-removed', data: { target: saleItem, priceChange: priceChange, targetType: 'sale_item' } });
    }

    public removeCustomerPriceChangeFromSale() {
        const targetDescription = this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.CLIENT_DISCOUNT');

        //Remove customer discount and update details if there are changes
        this.activeSaleStore.removeSalePriceChange((pc) => (pc.description === targetDescription) || !!pc.prize_id);

        for (const saleItem of this.currentSale?.sale_items || []) {
            if (saleItem.prize_id) {
                this.removeSaleItem(saleItem);
            }
        }
    }

    public async addDynamicItemToSale(department: Departments, price: number, overrides?: Partial<SalesItems>) {
        await this.awaitSale();

        const saleItem = this.saleUtilsService.getDynamicSaleItemTemplate(department, price);
        Object.assign(saleItem, overrides || {});

        return this.addSaleItem(saleItem);
    }

    public async addTipToSale(amount?: number) {
        if(!amount) {
            return;
        }

        const tipSaleItem = await this.saleUtilsService.getTipSaleItem(amount);

        return this.addSaleItem(tipSaleItem);
    }

    public async applyPriceList(priceList: number) {
        const categoriesMap = await this.entityManagerService.categories.fetchCollectionOffline().then(c => keyBy(c, (c) => c.id));
        const departmentsMap = await this.entityManagerService.departments.fetchCollectionOffline().then(d => keyBy(d, (d) => d.id));
        const itemsMap: Record<number, Items> = await this.util.getItemsFromIds(this.currentSale.sale_items);

        const priceListStr = `price${priceList}` as (`price${number}` & keyof Items & keyof ItemsCombinations);

        for (const saleItem of this.currentSale?.sale_items || []) {
            // Ignore items that have a parent row
            if (saleItem.sale_item_parent_uuid) {
                continue;
            }

            let changes: Partial<SalesItems> = {};
            const isGift = saleItem.price_changes?.some(pc => pc.type === 'gift');

            if (saleItem.item_id) {
                const item = itemsMap[saleItem.item_id];

                if (!item) {
                    continue;
                }

                const newPrice = saleItem.combination_id
                    ? item.combinations?.find((c) => c.id === saleItem.combination_id)?.[priceListStr]
                    : item[priceListStr];

                if (!Number.isFinite(newPrice)) {
                    continue;
                }

                changes = {
                    price: newPrice
                };

                //Change the department (unless we have a gift discount)
                if (!isGift) {
                    const targetDepartment = priceList === 1 ? `department` : `department${priceList}` as (`department${number}` & keyof Items);

                    changes = {
                        ...changes,
                        department: item[targetDepartment] || item['department']
                    };
                }
            } else if (saleItem.category_id) {
                //Change the department (unless we have a gift discount)
                if (!isGift) {
                    const category = categoriesMap[saleItem.category_id];

                    if (!category) {
                        continue;
                    }

                    const targetDepartment = priceList === 1 ? category[`department_id`] : category[`department${priceList}_id` as (`department${number}` & keyof Categories)];

                    if (!targetDepartment) {
                        continue;
                    }

                    const department = departmentsMap[targetDepartment];

                    if (!department) {
                        continue;
                    }

                    changes = {
                        ...changes,
                        department: department
                    };
                }
            }

            if (Object.keys(changes).length) {
                this.editSaleItem(saleItem, changes);
            }
        }
    }

    public async addCustomer(customer: Customers | SalesCustomer) {
        await this.awaitSale();

        //Prepare sale customer
        const saleCustomer = <SalesCustomer>structuredClone(customer);

        if (validateUuid(String(saleCustomer.id || ''))) {
            Object.assign(saleCustomer, {
                id: null,
                uuid: saleCustomer.id,
                customer_id: null
            });
        } else {
            Object.assign(saleCustomer, {
                id: null,
                customer_id: saleCustomer.id
            })
        }

        //Add customer to sale
        this.updateSaleDetails({
            customer_tax_code: undefined,
            lottery_code: (<Customers>customer).lottery_code || this.currentSale.lottery_code,
            sale_customer: saleCustomer
        });

        //Manage customer discounts
        this.removeCustomerPriceChangeFromSale();

        if (saleCustomer.discount_perc) {
            this.addSaleDiscountPerc(saleCustomer.discount_perc, this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.CLIENT_DISCOUNT'));
        }

        this.sendActiveSaleEvent({ event: 'customer-added', data: saleCustomer });

        this.cleanPaymentsFromSale();

        return saleCustomer;
    }

    public updateSaleCustomer(customer: Partial<SalesCustomer>) {
        const { uuid, ...saleCustomer } = customer;

        const updatedCustomer: SalesCustomer = {
            ...{ uuid },
            ...(this.currentSale?.sale_customer || {}),
            ...saleCustomer
        };

        return this.addCustomer(updatedCustomer);
    }

    public removeCustomer() {
        if (this.currentSale?.sale_customer?.default_pricelist) {
            const { sale_customer, ...sale } = this.currentSale;
            this.determinePricelistToUse(structuredClone(sale));
        }

        this.activeSaleStore.removeSaleCustomer();

        this.updateSaleDetails({
            customer_tax_code: undefined,
        });

        this.sendActiveSaleEvent({ event: 'customer-removed' });

        this.removeCustomerPriceChangeFromSale();
    }

    public async addTaxCode(taxCode: string) {
        await this.awaitSale();

        this.updateSaleDetails({
            sale_customer: undefined,
            customer_tax_code: `${taxCode}`.toUpperCase()
        });
    }

    public hasActiveSaleItem() {
        return !!(this.currentSaleItem?.saleUuid && this.currentSaleItem.saleUuid === this.currentSale.uuid);
    }

    public isActiveSaleItem(saleItem?: SalesItems) {
        // Return false if no item is selected
        if (!this.currentSaleItem) {
            return false;
        }

        const currentUuid = this.currentSaleItem.uuid;

        if (saleItem) {
            // if a sale item is provided, check if it is the same as the current selected item
            return currentUuid === saleItem.uuid;
        } else {
            // if no sale item is provided, check if the current selected item is in the sale
            saleItem = this.currentSale.sale_items?.find(item => item.uuid === currentUuid);

            if (saleItem) {
                return saleItem.id || saleItem.quantity > 0;
            }
        }

        // If we reach this point, the current selected item is not in the sale. Clean-up and return false
        this.currentSaleItem = undefined;

        return false;
    }

    public getActiveSaleItem = (saleItem?: SalesItems) => {
        return saleItem || this.currentSale.sale_items?.find(i => i.uuid === this.currentSaleItem?.uuid);
    }

    public selectActiveSaleItem(saleItem?: SalesItems) {
        //Disable selection on sale items that are part of a menu
        if (saleItem?.sale_item_parent_uuid) {
            return;
        }

        this.currentSaleItem = this.currentSaleItem?.uuid === saleItem?.uuid ? undefined : { saleUuid: this.currentSale.uuid!, uuid: saleItem?.uuid! };

        return this.currentSaleItem;
    }

    public addSaleSurchargeFix(value: number, description?: string, index?: number, overrides?: Partial<SalesPriceChanges>) {
        this.addGenericPriceChange(this.currentSale, 'surcharge_fix', value, description || this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.SURCHARGE'), index, overrides);
    }

    public addSaleDiscountFix(value: number, description?: string, index?: number, overrides?: Partial<SalesPriceChanges>) {
        const discountableAmount = this.fiscalUtils.getDiscountableAmount(this.currentSale);
        
        if (value > discountableAmount) {
            throw { translationId: 'DISCOUNT_TOO_HIGH', translationParams: { amount: this.tilbyCurrencyPipe.transform(discountableAmount) } };
        }

        this.addGenericPriceChange(this.currentSale, 'discount_fix', value, description || this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.DISCOUNT'), index, overrides);
    }

    public addSaleSurchargePerc(value: number, description?: string, index?: number, overrides?: Partial<SalesPriceChanges>) {
        this.addGenericPriceChange(this.currentSale, 'surcharge_perc', value, description || this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.SURCHARGE'), index, overrides);
    }

    public addSaleDiscountPerc(value: number, description?: string, index?: number, overrides?: Partial<SalesPriceChanges>) {
        if (value > 100) {
            throw 'DISCOUNT_OVER_100';
        }

        this.addGenericPriceChange(this.currentSale, 'discount_perc', value, description || this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.DISCOUNT'), index, overrides);
    }

    public addSaleItemSurchargeFix(value: number, description?: string, index?: number, overrides?: Partial<SalesItemsPriceChanges>) {
        const saleItem = this.getActiveSaleItem();

        if (!saleItem) {
            return;
        }

        if(this.fiscalUtils.getSaleItemDiscountability(saleItem) === 'tip') {
            throw 'CANNOT_ADD_PRICE_CHANGES_ON_TIPS';
        }

        this.addGenericPriceChange(saleItem, 'surcharge_fix', value, description || this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.SURCHARGE'), index, overrides);
    }

    public addSaleItemDiscountFix(value: number, description?: string, index?: number, overrides?: Partial<SalesItemsPriceChanges>) {
        const saleItem = this.getActiveSaleItem();

        if (!saleItem) {
            return;
        }

        if (saleItem.final_price === 0) {
            throw 'NO_DISCOUNT_WITH_FREE_ITEM';
        }

        if (value > saleItem.final_row_price!) {
            throw 'NO_DISCOUNT_HIGHER_THEN_AMOUNT';
        }

        if (saleItem.not_discountable) {
            throw 'ITEM_NOT_DISCOUNTABLE';
        }

        this.addGenericPriceChange(saleItem, 'discount_fix', value, description || this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.DISCOUNT'), index, overrides);
    }
    public addSaleItemSurchargePerc(value: number, description?: string, index?: number, overrides?: Partial<SalesItemsPriceChanges>) {
        const saleItem = this.getActiveSaleItem();

        if (!saleItem) {
            return;
        }

        if(this.fiscalUtils.getSaleItemDiscountability(saleItem) === 'tip') {
            throw 'CANNOT_ADD_PRICE_CHANGES_ON_TIPS';
        }

        this.addGenericPriceChange(saleItem, 'surcharge_perc', value, description || this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.SURCHARGE'), index, overrides);
    }
    public addSaleItemDiscountPerc(value: number, description?: string, index?: number, overrides?: Partial<SalesItemsPriceChanges>) {
        const saleItem = this.getActiveSaleItem();

        if (!saleItem) {
            return;
        }

        if (value > 100) {
            throw 'DISCOUNT_OVER_100';
        }

        if (saleItem.final_price === 0) {
            throw 'NO_DISCOUNT_WITH_FREE_ITEM';
        }

        if (saleItem.not_discountable && !(this.configurationManagerService.getSetting("cashregister.allow_gift_not_discountable") && value === 100)) {
            throw 'ITEM_NOT_DISCOUNTABLE';
        }

        this.addGenericPriceChange(saleItem, 'discount_perc', value, description || this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.DISCOUNT'), index, overrides);
    }

    public async addPrize(prize: ChainPrizes) {
        switch (prize.type) {
            case 'discount_fix':
            case 'discount_perc':
                this.addGenericPriceChange(this.currentSale, prize.type, prize.discount_amount!, prize.name, undefined, { prize_id: prize.id });
                break;
            case 'gift':
                const giftItem = await this.entityManagerService.items.fetchCollectionOffline({ sku: prize.item_sku }).then(items => items[0]);

                if (giftItem) {
                    const saleItem = await this.addItemToSale(giftItem, 1, undefined, 1, undefined, { prize_id: prize.id });
                    this.giftSaleItem(saleItem);
                }
                break;
        }
    }

    public giftSaleItem(saleItem?: SalesItems, description?: string, newDepartment?: Departments) {
        const targetSaleItem = this.getActiveSaleItem(saleItem);

        if (!targetSaleItem) {
            return;
        }

        if (targetSaleItem.not_discountable && !this.configurationManagerService.getSetting("cashregister.allow_gift_not_discountable")) {
            throw 'CASHREGISTER.ACTIVE_SALE_MODEL.ITEM_NOT_DISCOUNTABLE';
        }

        const giftPriceChange = this.saleUtilsService.getGenericPriceChange(targetSaleItem, 'gift', 100, description || this.translateService.instant('CASHREGISTER.ACTIVE_SALE.GIFT'));

        this.activeSaleStore.addSaleItemPriceChanges(targetSaleItem.uuid, [giftPriceChange], { replace: true });

        if (newDepartment) {
            this.editSaleItem(targetSaleItem, {
                department: newDepartment
            });
        }
    }

    public checkSale() { //Checks the sale before printing a document
        const errors: Record<string, boolean> = {};
        const warnings: Record<string, boolean> = {};

        const hasNegativeAmount = (this.currentSale?.final_amount || 0) < 0;
        const saleItems = this.currentSale.sale_items || [];

        if (!saleItems.length) {
            errors["SALE_EMPTY"] = true;
        } else {
            for (const saleItem of saleItems) {
                if (saleItem.request_weighing) {
                    errors["REQUEST_WEIGHING"] = true;
                }
                if (saleItem.quantity > 0) {
                    if (hasNegativeAmount) {
                        errors["NEG_AMOUNT_WITH_SALES"] = true;
                    }
                    if (saleItem.$partialAmount && saleItem.$partialAmount < 0) {
                        errors["NEG_SALE_ROWS"] = true;
                    }
                }
                if (!saleItem.price && saleItem.type === 'sale' && !saleItem.sale_item_parent_uuid) {
                    if (!this.configurationManagerService.getPreference('cashregister.hide_zero_price_warnings')) {
                        warnings["ZERO_PRICE_ITEMS"] = true;
                    }
                }
            }
        }

        const errorKeys = Object.keys(errors);
        const warningKeys = Object.keys(warnings);

        return {
            isValid: !errorKeys.length,
            hasWarnings: !!warningKeys.length,
            errors: errorKeys,
            warnings: warningKeys
        };
    }

    public async setPrinterDocumentData(data: DocumentPrinterOptions) {
        ActiveSaleService.printerDocumentDataUpdates.next(data);

        if (this.isActiveSale() && this.currentSale.e_invoice != null && !this.isEInvoiceDocument(data?.document_type?.id)) {
            try {
                const answer = await this.confirmDialogService.openDialog({
                    data: {
                        messageLabel: 'CASHREGISTER.ACTIVE_SALE.REMOVE_SALE_E_INVOICE_PROMPT'
                    }
                });

                if (answer) {
                    this.activeSaleStore.removeEInvoice();
                } else {
                    const newDocumentData = await this.documentPrintersManager.getPrinterDocumentData(data?.printer?.id || 'default', ['e_invoice'], data.options);
                    ActiveSaleService.printerDocumentDataUpdates.next(newDocumentData);
                }
            } catch (error) { }
        }
    }


    public async resetDocumentData() {
        try {
            let defaultDocumentData = await this.documentPrintersManager.getPrinterDocumentData('default', 'default');
            await this.setPrinterDocumentData(defaultDocumentData);
        } catch (error) {
            this.printerDocumentData = undefined;
            throw error;
        }
    }

    public setEInvoiceData(eInvoiceData: SalesEInvoice) {
        this.updateSaleDetails({ e_invoice: eInvoiceData });
    }

    public async verifyPrinterDocumentDataAndSelect() {
        try {
            let pDocData = this.printerDocumentData;

            if (pDocData?.printer && pDocData?.document_type) {
                await this.documentPrintersManager.getPrinterDocumentData(pDocData.printer.id!, (pDocData?.document_template?.id || pDocData.document_type.id as any), (pDocData.options || {}));
            } else {
                await this.resetDocumentData();
            }
        } catch (err) {
            const printerDocumentData = await this.documentPrintersManager.openDialog();

            if (printerDocumentData) {
                this.setPrinterDocumentData(printerDocumentData);
            } else {
                throw err;
            }
        }
    }

    private async getCashPayment(sale: Sales): Promise<SalesPayments> {
        const cashPayment = await this.entityManagerService.paymentMethods.fetchCollectionOffline({ payment_method_type_id: 1 }).then(methods => methods[0]);

        if (!cashPayment) {
            throw 'CASHREGISTER.ACTIVE_SALE.NO_CASH_PAYMENT_AVAILABLE';
        }

        const time = new Date();

        return {
            amount: this.getFastPaymentAmount(sale, cashPayment),
            //@ts-expect-error
            date: time.toISOString(),
            payment_method_id: cashPayment.id,
            payment_method_name: cashPayment.name,
            payment_method_type_id: 1,
            payment_method_type_name: this.translateService.instant('CASHREGISTER.ACTIVE_SALE_MODEL.CASH'),
            unclaimed: false,
        };
    }

    public async cashPayment() {
        const payment = await this.getCashPayment(this.currentSale);

        this.updateSaleDetails({
            payments: [payment]
        });

        return this.processPayments();
    }

    public async addGenericPayment(paymentMethod: PaymentMethods, amount?: number, paymentInputObject?: Partial<SalesPayments>, options?: { clearPreviousPayments?: boolean }) {
        const availableType = paymentMethodTypes.find((type) => type.id === paymentMethod.payment_method_type_id);

        if (!availableType) {
            throw 'CASHREGISTER.ACTIVE_SALE_MODEL.NO_TYPE_FOR_PAYMENT_METHOD';
        }

        if (this.currentSale?.sale_customer && paymentMethod.payment_method_type_id !== 16 && this.configurationManagerService.getPreference('cashregister.payments.force_prepaid_credit')) {
            const totalCredit = this.currentSale.sale_customer.$prepaidCredit || (await this.getCustomerPrepaidInfo())?.total_credit || 0;
            const usedCredit = (this.currentSale.payments || []).filter((p) => p.payment_method_type_id === 16).reduce((sum, p) => sum + p.amount, 0) || 0;

            if (totalCredit - usedCredit > 0) {
                throw 'CASHREGISTER.ACTIVE_SALE_MODEL.USE_PREPAID_CREDIT';
            }
        }

        const time = new Date();

        const payment = Object.assign({}, paymentInputObject || {}, <SalesPayments>{
            amount: amount || this.getAmountLeftToPay({ targetPayment: paymentMethod }),
            date: (time.toISOString() as unknown as Date),
            payment_method_id: paymentMethod.id,
            payment_method_name: paymentMethod.name,
            payment_condition_code: paymentMethod.payment_condition?.code,
            payment_method_type_id: availableType.id,
            payment_method_type_name: availableType.name,
            unclaimed: this.fiscalUtils.isMethodUnclaimed(availableType.id, paymentMethod.unclaimed),
        });

        const currentPayments = Array.isArray(this.currentSale?.payments) && !options?.clearPreviousPayments ? this.currentSale.payments.filter((p) => p.amount) : [];

        this.updateSaleDetails({
            payments: [...currentPayments, payment]
        });
    }

    public skipTransactionsSend() {
        return this.activeSaleStore.markPendingTransactionsAsSkipped();
    }

    public async sendTransactions(): Promise<OrderSendStatus[]> {
        if (!this.currentSale?.uuid) {
            return [];
        }

        this.printDocumentInProgress.set(true);

        try {
            //Save pending transaction first (use offline-mode as the sendTransactions will also save the transaction in offline-first mode)
            await this.activeSaleStore.saveCurrentTransaction({ offlineMode: true });

            const results = await this.salePrintingUtils.sendTransactions(this.currentSale.uuid);

            return results;
        } finally {
            this.printDocumentInProgress.set(false);
        }
    }

    /**
     * Retrieves the current sale details along with its transactions.
     *
     * @param {Object} [options] - Options for retrieving sale details.
     * @param {boolean} [options.includeCurrentChanges] - Whether to include current (unsaved) transaction changes.
     * @return {[Sales, SaleTransactions[]] | []} An array containing the sale and its transactions, or an empty array if there is no sale.
     */
    public getSaleChangesDetails(options?: { includeCurrentChanges: boolean }) {
        return this.activeSaleStore.getSaleChangesDetails(options);
    }

    public async reprintOrder(): Promise<OrderSendStatus[]> {
        if (!this.currentSale?.uuid) {
            return [];
        }

        this.printDocumentInProgress.set(true);

        try {
            const results = await this.salePrintingUtils.sendSaleAsOrder(this.currentSale, { reprint: true });

            return results;
        } finally {
            this.printDocumentInProgress.set(false);
        }
    }

    public async sendExitToPrinter(exit: Exit): Promise<OrderSendStatus[]> {
        if (!exit.nameValue) {
            return [];
        }

        const opData = this.operatorManager.getOperatorData();

        //Create a copy of the sale for the print function
        const sale = structuredClone(this.currentSale);

        Object.assign(sale, {
            seller_id: opData.id,
            seller_name: opData.full_name,
        });

        //Send the order to the printer
        return this.salePrintingUtils.sendExitToPrinter(sale, exit.nameValue);
    }

    public async printNonFiscalSale(printer?: Printers) {
        if (!this.isActiveSale()) {
            return;
        }

        if (!printer) {
            printer = this.printerDocumentData?.printer;
        }

        if (!printer) {
            throw 'NO_PRINTER_SELECTED';
        }

        //Freeze the sale to prevent it from being changed by the user
        const saleToPrint = structuredClone(this.currentSale);

        try {
            this.printDocumentInProgress.set(true);
            await this.salePrintingUtils.printNonFiscalSale(saleToPrint, printer);
            if (this.configurationManagerService.getPreference('cashregister.open_tables_after_send')) {
                this.goToTablesSelectedRoom(saleToPrint.room_id, saleToPrint.order_type);
            }
        } finally {
            this.printDocumentInProgress.set(false);
        }
    }

    public async emitDocument(sale?: Sales, printerDocumentData?: DocumentPrinterOptions, options?: EmitDocumentOptions): Promise<EmitDocumentResult> {
        let result: EmitDocumentResult = {}; //TODO: fix typing

        const opData = this.operatorManager.getOperatorData();

        const targetSale = sale || this.currentSale;
        const saleToEmit = Object.assign(structuredClone(targetSale), { seller_name: opData.full_name, seller_id: opData.id }) as SalesCashregister;
        const isActiveSale = targetSale.uuid === this.currentSale.uuid;

        const docData = structuredClone(printerDocumentData || this.printerDocumentData!);
        const isKioskSale = saleToEmit.channel === 'kiosk' && (this.$state.current.name.startsWith('app.kiosk') || this.configurationManagerService.getPreference('kiosk.payments.send_sale_to_order_printers'));

        const printerId = docData.printer.id;
        const docId = docData.document_type.id;

        if (isActiveSale) {
            this.printDocumentInProgress.set(true);
        }

        try {
            if (this.isEInvoiceDocument(docId)) {
                try {
                    await this.activeSaleStore.saveSale({ onlineFirstMode: true });
                } catch (err) {
                    throw 'E_INVOICE_OFFLINE';
                }
            }

            //Convert quick coupons in tickets
            await this.salePayment.quickCouponsToTickets(saleToEmit);

            const printResult = await this.documentPrinter.printDocument(saleToEmit, docData);

            if (!Array.isArray(printResult)) {
                this.finishSaleClosing(printResult);

                result = {
                    emittedSale: printResult
                }
            } else {
                try {
                    let notes = [];

                    let now = moment();
                    let documentDate = saleToEmit.sale_documents?.[0]?.date;

                    if (documentDate) {
                        let documentDateObj = moment(documentDate);

                        if (!documentDateObj.isBetween(moment(now).subtract(10, 'minutes'), moment(now).add(10, 'minutes'))) {
                            notes.push("CHECK_PRINTER_CLOCK");
                        }
                    }

                    if (saleToEmit.is_summary && !saleToEmit.sale_documents?.find((saleDocument) => ['summary_invoice', 'summary_e_rc', 'summary_e_nrc', 'generic_invoice'].includes(saleDocument.document_type))) {
                        saleToEmit.is_summary = false;
                    }

                    Object.assign(saleToEmit, {
                        status: 'closed',
                        closed_at: now.toISOString(),
                        change: this.getChange(saleToEmit),
                        secondary_final_amount: saleToEmit.secondary_exchange_rate ? ((saleToEmit.final_amount || 0) * saleToEmit.secondary_exchange_rate) : null
                    });

                    if (!saleToEmit.channel || saleToEmit.channel === 'pos') {
                        const deliveryChannel = saleToEmit.payments?.find((payment) => payment.payment_method_type_id === 17);

                        if (deliveryChannel && deliveryChannel.payment_data) {
                            saleToEmit.channel = deliveryChannel.payment_data;
                        }
                    }

                    const sellerData = {
                        seller_id: opData.id,
                        seller_name: opData.full_name
                    };

                    if (!saleToEmit.seller_id) {
                        Object.assign(saleToEmit, sellerData);
                    }

                    await this.saleUtilsService.calculateSaleItemsCosts(saleToEmit);
                    const sale = await this.saveSaleToStorage(saleToEmit);

                    this.sendActiveSaleEvent({ event: 'sale-closed', data: structuredClone(saleToEmit) });

                    this.finishSaleClosing(sale!);

                    Object.assign(result, {
                        emittedSale: saleToEmit,
                        notes: notes
                    });
                } catch (error: any) {
                    result = {
                        error: this.translateService.instant('CASHREGISTER.ACTIVE_SALE.ERROR_WHILE_SAVING_AFTER_PRINT')
                    }
                }
            }
        } catch (msg_error: any) {
            result = {
                printError: msg_error,
                printerId: printerId
            }
        }

        if (isActiveSale) {
            this.printDocumentInProgress.set(false);

            if (this.printerDocumentData) {
                this.printerDocumentData.tail = undefined;
            }
        }

        if ('emittedSale' in result) {
            if (isActiveSale) {
                this.lastSaleTotal.set({
                    final_amount: this.salePayment.getTotalAmountToPay(targetSale),
                    change: targetSale.change || 0,
                    id: typeof targetSale.id === 'number' ? targetSale.id : undefined,
                    uuid: targetSale.uuid!
                });
            }

            const printNFSaleOptions = {
                isAutoPrint: true
            };

            const isPrintOrdersOnSaleCloseEnabled = !!this.configurationManagerService.getPreference('cashregister.print_orders_on_sale_close');
            const isSaleAPrintableOrder = !saleToEmit.table_id && !['delivery', 'take_away'].includes(saleToEmit.order_type!); //Exclude sales with a table or take away/delivery
            const isSaleAutoPrintable = !!(saleToEmit.auto_print_order && options?.batchOrderMode); //For external sales

            //Set the source cashregister if this sale is not from a kiosk and the auto order print is not enabled. The order will be printed only on the order printers that are linked to the printer that emitted the sale
            if (!isKioskSale && !isPrintOrdersOnSaleCloseEnabled && !isSaleAutoPrintable) {
                Object.assign(printNFSaleOptions, { sourceCashregister: printerId });
            }

            if (targetSale.final_amount! >= 0 && (isKioskSale || isSaleAutoPrintable || isSaleAPrintableOrder)) {
                this.salePrintingUtils.sendSaleAsOrder(result.emittedSale, printNFSaleOptions, docData.printer);
            }
        }

        return result;
    }

    public isCreditNote() {
        return this.saleUtilsService.isCreditNote(this.currentSale);
    }

    private saveSplitSaleData = (sale: SalesCashregister) => {
        if(!sale._split_sale_data) {
            return;
        }

        const transaction = this.saleTransactionUtilsService.createSaleTransaction(sale, this.operatorManager.getOperatorData());
        transaction.sale_details = transaction.sale_details || {};

        Object.assign(transaction.sale_details, {
            _split_sale_data: sale._split_sale_data
        });

        return this.saleTransactionUtilsService.saveTransaction(transaction);
    }


    public async splitSale(options?: SplitSaleDialogOptions) {
        if (!this.currentSale.sale_items?.length) {
            return;
        }

        this.lockPaymentButtons.set(true);

        //If we split by amount or covers, will contain the original sale and will be stored
        let saleToStore;

        try {
            //Save the current changes (if any)
            await this.saveSaleToStorage();

            //Show the split dialog
            const splitResult = await this.splitSaleDialog.openDialog({ data: { currentSale: this.currentSale, options } });

            if(splitResult) {
                let saleToOpen;
    
                if ('originalSaleTransaction' in splitResult) { //There are changes to apply via transactions (split by items)
                    await this.entityManagerService.saleTransactions.postOneOfflineFirst(splitResult.originalSaleTransaction);
                } else if (splitResult.originalSale) { //The original sale is changed entirely (split by covers or amount)
                    saleToStore = Object.assign(structuredClone(this.currentSale), {
                        split_sale_origin_uuid: this.currentSale.uuid,
                        notes: "Conto separato"
                    });

                    if(splitResult.originalSale.sale_items?.length) {
                        saleToOpen = await this.saveSaleToStorage(splitResult.originalSale);
                        await this.saveSplitSaleData(splitResult.originalSale);
                    }
                }

                if (splitResult.splitSale) {
                    saleToOpen = await this.saveSaleToStorage(splitResult.splitSale);
                    await this.saveSplitSaleData(splitResult.splitSale);
                }

                if (saleToOpen) {
                    await this.loadSale(saleToOpen.uuid, { skipOperatorCheck: true });
                }
            }
        } catch (err) {

        } finally {
            this.lockPaymentButtons.set(false);

            if (saleToStore) {
                this.performStore(saleToStore, { skipParentCheck: true, closeActiveSale: false, isOriginalSplit: true });
            }
        }
    }

    /**
     * Moves the items of the current sale to another sale, deletes the current sale and opens the target sale
     * @param saleUuid the uuid of the sale to move the items to
     */
    public async mergeSales(saleUuid: string) {
        //Check if we have an active sale and if it is not the same as the one to merge
        if (!this.isActiveSale() || saleUuid === this.currentSale.uuid) {
            return;
        }

        //Fetch target sale (will be used to get current info like covers)
        const targetSale = (await this.saleTransactionUtilsService.fetchSalesFromTransactions({ uuid: saleUuid }))[0];

        if (!targetSale) {
            return;
        }

        const currentSale = structuredClone(this.currentSale);

        //Create a transaction from the target sale (remove the details ad we don't need them)
        const targetSaleTransaction = this.saleTransactionUtilsService.saleToTransaction(currentSale);
        targetSaleTransaction.skip_printing = true;
        targetSaleTransaction.sale_id = undefined;
        targetSaleTransaction.sale_uuid = saleUuid;

        //Configure covers
        const targetSaleCovers = ((targetSale.covers || 0) + (currentSale.covers || 0) || undefined)

        targetSaleTransaction.sale_details = {
            covers: targetSaleCovers
        };

        if (targetSaleCovers) {
            const coverConfig = await this.saleUtilsService.getCoverConfiguration(currentSale);

            if (coverConfig.type === 'item') {
                const itemId = coverConfig.data.id!;

                //Remove the item from the target sale transaction
                targetSaleTransaction.sale_items_transactions = targetSaleTransaction.sale_items_transactions?.filter((item) => item.sale_item_details?.item_id !== itemId) || [];

                //Check if the item is already in the target sale and, if so, adjust the quantity.
                const existingItem = targetSale.sale_items?.find((item) => item.item_id === itemId);

                if (existingItem) {
                    const coverItemTransaction = this.saleTransactionUtilsService.createSaleItemTransaction(existingItem);
                    coverItemTransaction.quantity_difference = targetSaleCovers - existingItem.quantity;
                    targetSaleTransaction.sale_items_transactions?.push(coverItemTransaction);
                }
            }
        }

        //Cleanup sub entities
        for (const saleItem of targetSaleTransaction.sale_items_transactions || []) {
            for (const entity of ['variations', 'ingredients', 'price_changes']) {
                if (saleItem.sale_item_details?.[entity]) {
                    saleItem.sale_item_details[entity] = this.saleUtilsService.getCleanSubEntity(saleItem.sale_item_details[entity]);
                }
            }
        }

        //Delete the current sale
        await this.deleteSale({ skipClosing: true, sale: currentSale });

        //Add the target sale transaction
        await this.entityManagerService.saleTransactions.postOneOfflineFirst(targetSaleTransaction);

        this.printSaleTableVariation(currentSale, targetSale);

        //Load the target sale
        await this.loadSale(saleUuid);
    }

    public isPaymentsOnSaleEmpty() {
        return !(this.currentSale?.payments?.length);
    }

    public removePaymentFromSale(payment: SalesPayments) {
        this.updateSaleDetails({
            payments: this.currentSale?.payments?.filter(p => p !== payment) || []
        });
    }

    public cleanPaymentsFromSale() {
        const currentPayments = this.currentSale?.payments || [];
        const newPayments = currentPayments.filter(p => p.paid);

        if (newPayments.length !== currentPayments.length) {
            this.updateSaleDetails({
                payments: newPayments
            });
        }
    }

    public getTotalAmountToPay() {
        return this.salePayment.getTotalAmountToPay(this.currentSale);
    }

    public getAmountLeftToPay(options?: { targetPayment: PaymentMethods }) {
        return this.salePayment.getAmountLeftToPay(this.currentSale, options);
    }

    public getPaid() {
        return this.salePayment.getPaid(this.currentSale);
    }

    public getChange(targetSale: Sales) {
        if (!targetSale) {
            targetSale = this.currentSale;
        }

        return this.salePayment.getSaleChange(targetSale);
    }

    public async processSaleChange() {
        const targetSale = this.currentSale as SalesCashregister;

        if (!targetSale) {
            return;
        }

        this.salePayment.processSaleChange(targetSale);

        //If the sale doesn't have change or we are not processing the current sale, do nothing
        if (!targetSale.change) {
            return;
        }

        //Process left change
        const departments = await this.entityManagerService.departments.fetchCollectionOffline();

        //Prepaid change check
        if (this.connection.isOnline() && this.prepaidSale.isEnabled() && targetSale.sale_customer && validateUuid(targetSale.sale_customer.uuid!) && targetSale.sale_customer.fidelity) {
            const rechargeDepartmentId = parseInt(this.configurationManagerService.getPreference('prepaid.recharge_department_id') || '') || 0;
            const rechargeDepartment = rechargeDepartmentId ? departments.find(department => department.id === rechargeDepartmentId) : undefined;

            if (rechargeDepartment) {
                const answer = await this.confirmDialogService.openDialog({
                    data: {
                        messageLabel: 'CASHREGISTER.ACTIVE_SALE_MODEL.WANT_TO_RELOAD_WITH_CHANGE',
                        messageParams: { value: this.tilbyCurrencyPipe.transform(targetSale.change) }
                    }
                });

                if (answer) {
                    await this.addDynamicItemToSale(rechargeDepartment, targetSale.change);

                    delete targetSale.change_type;
                    delete targetSale.change;

                    return;
                }
            }
        }

        if (targetSale.change_type === 'ticket' && this.configurationManagerService.getPreference('fiscalprinter.ticket_change')) {
            const ticketDepartment = departments.find(department => department.vat?.code === 'N2');

            if (ticketDepartment) {
                await this.addDynamicItemToSale(ticketDepartment, targetSale.change, { name: this.translateService.instant('CASHREGISTER.ACTIVE_SALE.TICKET_CHANGE_ITEM_NAME'), not_discountable: true });

                return;
            } else {
                throw 'MISSING_TICKET_CHANGE_DEPARTMENT';
            }
        }

        if (targetSale.change_type === 'ticket') {
            throw 'UNHANDLED_TICKET_CHANGE';
        }
    }

    public async addSignatureToSale() {
        if (!this.currentSale) {
            return;
        }

        const signature = await this.signaturePadDialog.openDialog({
            data: {
                canvasSize: { width: 800, height: 200 },
                showSignatureGuide: true
            }
        });

        if (signature) {
            this.activeSaleStore.updateSaleDetails({ customer_signature: signature });
        } else {
            throw null;
        }
    }

    public async processPayments() {
        if (!this.currentSale || !this.printerDocumentData) {
            return;
        }

        const paymentMethods = await this.entityManagerService.paymentMethods.fetchCollectionOffline();
        const paymentMethodsById = keyBy(paymentMethods, m => m.id);
        const paymentsInSale = [...new Set((this.currentSale.payments || []).map(p => p.payment_method_id))];

        //These payment methods have a signature request inside their payment flow, so we ignore them here
        const paymentsWithSignatureRequest = new Set([
            28,
            36,
            43
        ]);

        //Check if a payments requires a customer signature
        const requiresSignature = paymentsInSale.some(methodId => {
            const paymentMethod = paymentMethodsById[methodId];

            return paymentMethod && paymentMethod.require_signature && !paymentsWithSignatureRequest.has(paymentMethod.payment_method_type_id)
        });

        if (requiresSignature && this.currentSale.final_amount! >= 0) {
            await this.addSignatureToSale();
        }

        this.sendActiveSaleEvent({ event: 'payment-in-progress' });

        const {
            salePayments,
            tipSaleItems,
            priceChanges,
            error
        } = await this.salePayment.processPayments(this.currentSale, this.printerDocumentData);

        this.updateSaleDetails({
            payments: salePayments
        });

        if(tipSaleItems?.length) {
            for (const saleItem of tipSaleItems) {
                this.activeSaleStore.addSaleItem(saleItem);
            }
        }

        if(priceChanges?.length) {
            this.activeSaleStore.addSalePriceChanges(priceChanges);
        }

        if (error) {
            throw error;
        }
    }

    public hasOnlineDigitalPayment() {
        return this.salePayment.hasOnlineDigitalPayment(this.currentSale)
    }

    public async getCustomerPrepaidInfo() {
        const customer_uuid = this.currentSale.sale_customer?.uuid;
        const customer_fidelity = this.currentSale.sale_customer?.uuid;

        if (!customer_uuid || !customer_fidelity) {
            return;
        }

        const lastMovement: ChainPrepaidMovementsRead = await this.restManager.getList('prepaid_movements', { customer_uuid: customer_uuid, valid_to: 'null', pagination: 'false' })
            .then((movements: ChainPrepaidMovementsRead[]) => Array.isArray(movements) ? movements[0] : null)

        if (!lastMovement) {
            return {
                credit: 0,
                ticket_credit: 0,
                total_credit: 0
            };
        }

        return {
            credit: lastMovement.credit,
            ticket_credit: lastMovement.ticket_credit,
            total_credit: lastMovement.credit + lastMovement.ticket_credit
        };
    }

    public async checkPrepaidPaymentStatus(prepaidCardMethod: PaymentMethodsFE) {
        // Initially disable the prepaid card payment method in the UI. It will be re-enabled if conditions are met.
        prepaidCardMethod.$disabled = true;

        const customer_uuid = this.currentSale.sale_customer?.uuid || '';

        if (customer_uuid) {
            // Set an initial informational message indicating credit update is in progress.
            prepaidCardMethod.$info = this.translateService.instant('CASHREGISTER.PAYMENTS.UPDATE_CREDIT');

            try {
                // Attempt to fetch the customer's prepaid credit information.
                const creditStatus = await this.getCustomerPrepaidInfo();
                const creditLabel = this.tilbyCurrencyPipe.transform(creditStatus?.total_credit || 0);

                // If the customer has any total credit, enable the payment option.
                if (creditStatus?.total_credit) {
                    prepaidCardMethod.$disabled = false;
                }

                // Update the payment method's disabled status and informational message based on the credit status.
                prepaidCardMethod.$info = this.translateService.instant('CASHREGISTER.PAYMENTS.CREDIT', { value: creditLabel });
            } catch (err) {
                // If there's a connection error while fetching credit info, keep the payment method disabled and show an error message.
                prepaidCardMethod.$info = this.translateService.instant('CASHREGISTER.PAYMENTS.CONNECTION_ERROR');
            }
        } else {
            // If no customer is selected or they don't have fidelity show a message indicating this. Enable the button to allow choosing a customer
            prepaidCardMethod.$disabled = false;
            prepaidCardMethod.$info = this.translateService.instant('CASHREGISTER.PAYMENTS.CUSTOMER_NOT_SELECTED');
        }

        // Return the result object, indicating whether the payment can proceed and the customer's UUID if applicable.
        return {
            customer_uuid,
            $disabled: prepaidCardMethod.$disabled
        };
    }

    public async checkSaleConfirm(skipWarnings?: boolean) {
        let result = this.checkSale();

        if (!result.isValid) {
            let error = result.errors[0];
            let message;

            switch (error) {
                case "SALE_EMPTY":
                case "NEG_AMOUNT_WITH_SALES":
                case "NEG_SALE_ROWS":
                case "REQUEST_WEIGHING":
                    message = `CASHREGISTER.ACTIVE_SALE.${error}`;
                    break;
                default:
                    message = 'CASHREGISTER.ACTIVE_SALE.UNKNOWN_ERROR_CAPITAL';
            }

            await this.alertDialogService.openDialog({ data: { messageLabel: message } });

            throw null;
        }

        if (result.hasWarnings && !skipWarnings) {
            for (let warning of result.warnings) {
                let message;

                switch (warning) {
                    case "ZERO_PRICE_ITEMS":
                        message = 'CASHREGISTER.ACTIVE_SALE.ITEMS_WITH_PRICE_ZERO';
                        break;
                    default:
                        message = 'CASHREGISTER.ACTIVE_SALE.UNKNOWN_ERROR_MANAGE_CHECK';
                }

                let result = await this.confirmDialogService.openDialog({ data: { messageLabel: message } });

                if (!result) {
                    throw null;
                }
            }
        }
    };
}
